/**
 * XSS Sanitizer Utility
 * Provides functions to sanitize HTML content and prevent XSS attacks
 */

export interface SanitizeOptions {
  allowedTags?: string[];
  allowedAttributes?: string[];
  allowLinks?: boolean;
}

/**
 * Default configuration for label sanitization
 */
export const LABEL_SANITIZE_CONFIG: SanitizeOptions = {
  allowedTags: ['strong', 'em', 'b', 'i', 'u', 'span', 'small', 'sup', 'sub', 'br', 'a', 'div'],
  allowedAttributes: ['class', 'id', 'href', 'target', 'rel', 'style'],
  allowLinks: true
};

/**
 * Default configuration for help text sanitization
 */
export const HELP_TEXT_SANITIZE_CONFIG: SanitizeOptions = {
  allowedTags: ['strong', 'em', 'b', 'i', 'u', 'span', 'small', 'sup', 'sub', 'br', 'a'],
  allowedAttributes: ['class', 'id', 'href', 'target'],
  allowLinks: true
};

/**
 * Checks if a string contains HTML tags
 */
export function containsHtml(text: string): boolean {
  return /<[^>]*>/.test(text);
}

/**
 * Detects potentially dangerous XSS patterns
 */
export function detectXssPatterns(html: string): string[] {
  const patterns = [
    /javascript:/gi,
    /on\w+\s*=/gi,
    /<script[^>]*>/gi,
    /<iframe[^>]*>/gi,
    /<object[^>]*>/gi,
    /<embed[^>]*>/gi,
    /<form[^>]*>/gi,
    /vbscript:/gi,
    /data:text\/html/gi,
    /expression\(/gi
  ];

  const threats: string[] = [];
  
  patterns.forEach((pattern, index) => {
    if (pattern.test(html)) {
      const threatNames = [
        'JavaScript URL',
        'Event Handler',
        'Script Tag',
        'Iframe Tag',
        'Object Tag',
        'Embed Tag',
        'Form Tag',
        'VBScript URL',
        'Data URL HTML',
        'CSS Expression'
      ];
      threats.push(threatNames[index]);
    }
  });

  return threats;
}

/**
 * Sanitizes HTML content to prevent XSS attacks
 */
export function sanitizeHtml(html: string, options: SanitizeOptions = LABEL_SANITIZE_CONFIG): string {
  if (!html || typeof html !== 'string') {
    return '';
  }

  // Create a temporary DOM element to parse and sanitize the HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Remove all script tags
  const scripts = tempDiv.querySelectorAll('script');
  scripts.forEach(script => script.remove());

  // Get all elements for processing
  const allElements = tempDiv.querySelectorAll('*');
  
  allElements.forEach(element => {
    const tagName = element.tagName.toLowerCase();
    
    // Remove elements not in allowed tags list
    if (!options.allowedTags?.includes(tagName)) {
      // Keep text content but remove the element
      const textContent = element.textContent || '';
      const textNode = document.createTextNode(textContent);
      element.parentNode?.replaceChild(textNode, element);
      return;
    }

    // Remove dangerous attributes
    const dangerousAttrs = [
      'onload', 'onerror', 'onclick', 'onmouseover', 'onfocus', 
      'onblur', 'onchange', 'onsubmit', 'onmouseout', 'onkeyup',
      'onkeydown', 'onkeypress', 'onmousedown', 'onmouseup'
    ];
    
    // Remove all dangerous attributes
    dangerousAttrs.forEach(attr => {
      if (element.hasAttribute(attr)) {
        element.removeAttribute(attr);
      }
    });

    // Remove any attribute that starts with 'on' (event handlers)
    Array.from(element.attributes).forEach(attr => {
      if (attr.name.toLowerCase().startsWith('on')) {
        element.removeAttribute(attr.name);
      }
      
      // Only keep allowed attributes
      if (options.allowedAttributes && !options.allowedAttributes.includes(attr.name.toLowerCase())) {
        element.removeAttribute(attr.name);
      }
    });

    // Handle links specially
    if (tagName === 'a') {
      if (!options.allowLinks) {
        // Remove link but keep text content
        const textContent = element.textContent || '';
        const textNode = document.createTextNode(textContent);
        element.parentNode?.replaceChild(textNode, element);
        return;
      }
      
      // Sanitize href attribute
      const href = element.getAttribute('href');
      if (href) {
        // Only allow safe protocols
        if (!href.match(/^(https?:\/\/|mailto:|tel:|#)/)) {
          element.removeAttribute('href');
        }
        
        // Remove javascript: and other dangerous protocols
        if (href.toLowerCase().startsWith('javascript:') || 
            href.toLowerCase().startsWith('vbscript:') ||
            href.toLowerCase().startsWith('data:')) {
          element.removeAttribute('href');
        }
      }
    }

    // Remove src attributes that could be dangerous
    const src = element.getAttribute('src');
    if (src && (src.toLowerCase().startsWith('javascript:') || 
                src.toLowerCase().startsWith('vbscript:') ||
                src.toLowerCase().startsWith('data:'))) {
      element.removeAttribute('src');
    }
  });

  return tempDiv.innerHTML;
}

/**
 * Validates if HTML content is safe for use in labels
 */
export function isLabelHtmlSafe(html: string): { safe: boolean; threats: string[] } {
  const threats = detectXssPatterns(html);
  return {
    safe: threats.length === 0,
    threats
  };
}

/**
 * Validates if HTML content is safe for use in help text
 */
export function isHelpTextHtmlSafe(html: string): { safe: boolean; threats: string[] } {
  const threats = detectXssPatterns(html);
  return {
    safe: threats.length === 0,
    threats
  };
}

/**
 * Safe HTML renderer for labels
 */
export function sanitizeLabelHtml(html: string): string {
  return sanitizeHtml(html, LABEL_SANITIZE_CONFIG);
}

/**
 * Safe HTML renderer for help text
 */
export function sanitizeHelpTextHtml(html: string): string {
  return sanitizeHtml(html, HELP_TEXT_SANITIZE_CONFIG);
}
